/**
 * Flash Sale Status Component
 *
 * Displays flash sale status text for cart items
 * Shows start/end time for flash sale products
 *
 * @module @kidslabs/module-flash-sale/modules/quote/components/flash-sale-status
 */

import React, { memo, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import { useCartActionsV3 } from '@kidslabs/module-quote';
import { useFlashSaleStatus } from '../../hooks/use-flash-sale-status';
import styles from './flash-sale-status.module.css';
export interface FlashSaleStatusProps {
    /** Cart item containing product info */
    item?: {
        product?: {
            sku?: string;
        };
        quantity?: number;
    };
}

/**
 * FlashSaleStatus component
 * Shows flash sale timing information for cart items
 */
export const FlashSaleStatus: React.FC<FlashSaleStatusProps> = memo(props => {
    const { item } = props;
    const { t } = useTranslation('quote');

    const sku = item?.product?.sku ?? '';
    const quantity = item?.quantity;
    const { statusText, adjustableQuantity } = useFlashSaleStatus(sku, quantity);
    const { handleUpdateQuantity } = useCartActionsV3();

    const handleAdjust = useCallback(() => {
        if (adjustableQuantity == null) return;
        handleUpdateQuantity({ item, value: adjustableQuantity });
    }, [adjustableQuantity, handleUpdateQuantity, item]);

    if (!statusText) return null;

    return (
        <div className={styles.container}>
            <Text className={styles.textAction} data-testid="flash-sale-status">
                {statusText}
            </Text>
            <Button
                variant={'text'}
                showIf={adjustableQuantity != null}
                className={styles.adjustButton}
                onClick={handleAdjust}
            >
                {t('quote_text_adjust')}
            </Button>
        </div>
    );
});

FlashSaleStatus.displayName = 'FlashSaleStatus';
